import { forwardRef, useImperativeHandle, useState, lazy, Suspense } from "react";
const AddCafeDialog = lazy(() =>
  import("@/components/admin/AddCafeDialog").then((m) => ({ default: m.AddCafeDialog })),
);
import { PWAInstallDialog } from "@/components/PWAInstallDialog";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { SubmitCafeDialog } from "@/components/SubmitCafeDialog";
import { useAdminRole } from "@/hooks/useAdminRole";
import { usePWAInstall } from "@/hooks/usePWAInstall";

export type HeaderDialogsHandle = {
  openAddCafe: () => void;
  openInstall: () => void | Promise<void>;
  openFeedback: () => void;
  openSuggestCafe: () => void;
};

/**
 * Owns open/close state for the four global Header dialogs and exposes
 * typed opener methods to Header via ref.
 */
export const HeaderDialogs = forwardRef<HeaderDialogsHandle>((_, ref) => {
  const [addCafeOpen, setAddCafeOpen] = useState(false);
  const [iosDialogOpen, setIosDialogOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [suggestCafeOpen, setSuggestCafeOpen] = useState(false);

  const { isAdmin } = useAdminRole(false);
  const { canInstall, isIOS, install } = usePWAInstall();

  useImperativeHandle(
    ref,
    () => ({
      openAddCafe: () => setAddCafeOpen(true),
      openInstall: async () => {
        if (isIOS) {
          setIosDialogOpen(true);
        } else if (canInstall) {
          await install();
        }
      },
      openFeedback: () => setFeedbackOpen(true),
      openSuggestCafe: () => setSuggestCafeOpen(true),
    }),
    [isIOS, canInstall, install]
  );

  return (
    <>
      {isAdmin && (
        <Suspense fallback={null}>
          <AddCafeDialog open={addCafeOpen} onOpenChange={setAddCafeOpen} />
        </Suspense>
      )}
      <PWAInstallDialog open={iosDialogOpen} onOpenChange={setIosDialogOpen} />
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      <SubmitCafeDialog open={suggestCafeOpen} onOpenChange={setSuggestCafeOpen} entryPoint="header_menu" />
    </>
  );
});

HeaderDialogs.displayName = "HeaderDialogs";
